import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import "@css/temp_wanted_back.css";
import { PairPreferenceEditor } from "@components/feature/roster_wanted_back/PairPreferenceEditor";
import * as func from "@components/feature/roster_wanted/script";
import TableSelectDate from "@components/feature/roster_wanted_back/TableSelectDate";
import LoadingDots from "@components/LoadingDots";
import ModalSubmittedWantedlist from "@components/ModalSubmittedWantedlist";
import ErrorComponent from "@components/ui/error";
import { useIsAdmin, useLoginUser } from "@hooks/useAuth";
import { useUnsavedChanges } from "@hooks/weekly-off/useUnsavedChanges";
import { useQueryClient } from "@tanstack/react-query";
import { sortNurseListByRoleThenSequence } from "@utils/common-util.ts";
import type * as type from "@vTypes";
import type { NurseResponse } from "@vTypes/nurse-type";
import { images } from "@img/imagesMap";
import {
  Ban,
  Check,
  ClipboardList,
  Info,
  Sparkles,
  UsersRound,
  X,
} from "lucide-react";
import { createPortal } from "react-dom";
import HeadNurseHeader from "@/components/feature/roster_wanted_back/HeadNurseHeader";
import CustomModal from "@/components/modal/CustomModal";
import { toast } from "@/components/toast/CustomToast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { ShiftReturnType } from "@/hooks/roster-craete/useShiftsQuery";
import { useGroupStore } from "@/store/groupStore";
import { resolveShiftFromWantedEntry } from "@/utils/wanted-shift-util";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { WantedActionBar } from "../roster-wanted/components/WantedActionBar";
import { WantedAuthoringCard } from "../roster-wanted/components/WantedAuthoringCard";
import { WantedMonthlyMemo } from "../roster-wanted/components/WantedMonthlyMemo";
import { WantedStatusBanner } from "../roster-wanted/components/WantedStatusBanner";
import {
  useFinalSubmit,
  useSaveMonthlyMemo,
  useSaveWantedRequest,
  useRetractSubmission,
  useSaveCalendarData,
} from "../roster-wanted/hooks/useRosterWantedMutations";
import {
  useMonthlyMemoQuery,
  useRosterWantedResources,
} from "../roster-wanted/hooks/useRosterWantedQueries";
import { getWantedEditorState } from "../roster-wanted/utils/wantedEditorState";
import {
  splitWantedEntries,
  type AvoidEntriesByDate,
} from "../roster-wanted/utils/wantedEntries";
import type { WantedSubmissionStatus } from "../roster-wanted/utils/wantedStatus";

interface Type_ShiftEntry {
  shift: string;
  comment: string | null;
}
type CalendarData = Record<string, Type_ShiftEntry>;

interface SelectCalendarEntry {
  type: string;
  comment: string | null;
}

interface Type_SelectCalendar {
  [date: string]: SelectCalendarEntry;
}

type ShiftPreferenceMode = "wanted" | "avoid";
type CalendarDisplay = "all" | ShiftPreferenceMode;

function adjustTextareaHeight(element: HTMLTextAreaElement) {
  element.style.height = "auto";
  element.style.height = `${element.scrollHeight}px`;
}

function preferenceToSchedule(
  arrayLength: number,
  originArray: type.PreferencesAll | undefined
) {
  const result = Array<string>(arrayLength).fill("");
  if (originArray?.data.shift) {
    for (const [letter, dates] of Object.entries(originArray.data.shift)) {
      for (const day of Object.keys(dates).map(Number)) {
        result[day - 1] = letter;
      }
    }
  }
  return result;
}

const aideRequestExamples = [
  "8일 N, 12일 휴무를 희망합니다.",
  "10일은 E 근무를 피하고 싶습니다.",
];

function formatWantedDeadline(value: string | null | undefined): string {
  if (!value) return "마감일 없음";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "마감일 없음";

  const minute = date.getMinutes();
  const minuteLabel = minute === 0 ? "" : ` ${minute}분`;
  return `${date.getMonth() + 1}월 ${date.getDate()}일 ${date.getHours()}시${minuteLabel}`;
}

function formatWantedSubmissionAt(
  value: string | null | undefined
): string | null {
  if (!value) return null;

  const submittedAt = new Date(value);
  if (Number.isNaN(submittedAt.getTime())) return null;

  return new Intl.DateTimeFormat("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(submittedAt);
}

// 모달 타입
type ModalType =
  | { type: "submittedList"; isViewOnlyOff: boolean; showWeeklyOff: boolean }
  | { type: "totalExceeded" }
  | { type: "dateExceeded"; date: string }
  | {
      type: "replacement";
      dateStr: string;
      intent: ShiftPreferenceMode;
      comment: string;
      currentShiftCode: string;
      currentIntent: ShiftPreferenceMode;
      nextShiftCode: string;
    }
  | { type: "submit" }
  | { type: "retract" }
  | null;

export default function Roster_wanted() {
  const queryClient = useQueryClient();
  const loginUser = useLoginUser();
  const isAdmin = useIsAdmin();
  /** 전체 직원 원티드/신청 현황 — 수간호사·시스템 관리자만 */
  const canViewAllStaffWantedOverview = isAdmin || !!loginUser?.is_head_nurse;

  const [activeDate, setActiveDate] = useState<{
    year: number;
    month: number;
  } | null>(null);
  const [fullNurseSchema, setFullNurseSchema] = useState<NurseResponse[]>([]);
  const [nurseSchema_IdName, setNurseSchema_IdName] = useState<
    { nurse_id: string; name: string }[]
  >([]);

  const [fullWanted, setFullWanted] = useState<type.WantedAll[]>([]);

  const [preferenceSchedule, setPreferenceSchedule] =
    useState<type.PreferenceSchedule[]>();

  /* 개별 데이터 */

  // LLM 입력내용
  const [wantedInput, setWantedInput] = useState<string | null>("");
  const [isAideDialogOpen, setIsAideDialogOpen] = useState(false);
  // 제출 여부
  const [submittedStatus, setSubmittedStatus] =
    useState<WantedSubmissionStatus>("empty");

  // 테이블 관련
  const [selectedCalendarData, setSelectedCalendarData] =
    useState<Type_SelectCalendar>({});
  const [availableShifts, setAvailableShifts] = useState<ShiftReturnType>([]);
  const [defaultShiftCode, setDefaultShiftCode] = useState<ShiftReturnType>([]);

  const [preferenceData, setPreferenceData] =
    useState<type.PreferenceData | null>(null);

  // 캘린더 내 주휴 관련
  const [myWeeklyOffIndex, setMyWeeklyOffIndex] = useState<number | null>(null);

  // 달력 관련
  const [holidayDates, setHolidayDates] = useState<string[]>([]);

  const [isSubmitRequest, setIsSubmitRequest] = useState<boolean>(false);

  // 2번 탭
  // 체크박스 값 저장
  /** 미선택 — 하드코딩 D 제거, 캘린더 클릭 전 근무 유형 선택 필요 */
  const [selectedShiftType, setSelectedShiftType] = useState<string>("");
  const [isShiftPickerHighlighted, setIsShiftPickerHighlighted] =
    useState(false);
  const [calendarDisplay, setCalendarDisplay] =
    useState<CalendarDisplay>("all");
  const [activeIntent, setActiveIntent] = useState<ShiftPreferenceMode | null>(
    null
  );
  const [isWantedGuideVisible, setIsWantedGuideVisible] = useState(true);
  const [avoidShiftByDate, setAvoidShiftByDate] = useState<AvoidEntriesByDate>(
    {}
  );
  const [initialAvoidShiftByDate, setInitialAvoidShiftByDate] =
    useState<AvoidEntriesByDate>({});

  // const isExistWanted = (bookmarkData && bookmarkData.length > 0);
  const isExistWanted = submittedStatus !== "empty";

  // 페이지 이탈 방지
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialSelectedCalendarData, setInitialSelectedCalendarData] =
    useState<Type_SelectCalendar>({});

  const [initialWantedInput, setInitialWantedInput] = useState<string | null>(
    ""
  );

  const { selectedGroupId } = useGroupStore();

  // 모달관련 state
  const [confirmModal, setConfirmModal] = useState<ModalType>(null);
  const wantedButtonRef = useRef<HTMLButtonElement>(null);
  const missingShiftSelectionTimerRef = useRef<number | null>(null);
  const lastMissingShiftSelectionAtRef = useRef(0);

  useEffect(() => {
    return () => {
      if (missingShiftSelectionTimerRef.current !== null) {
        window.clearTimeout(missingShiftSelectionTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const mainContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement;

    if (!mainContainer) return;

    if (confirmModal !== null) {
      mainContainer.style.overflow = "hidden";
    } else {
      mainContainer.style.overflow = "";
    }

    return () => {
      mainContainer.style.overflow = "";
    };
  }, [confirmModal]);

  useEffect(() => {
    if (isSubmitRequest) {
      const mainContainer = document.querySelector(
        ".c_main__container"
      ) as HTMLElement;

      if (mainContainer) {
        mainContainer.style.overflow = "hidden";
      }

      return () => {
        if (mainContainer) {
          mainContainer.style.overflow = "";
        }
      };
    }
  }, [isSubmitRequest]);

  // 일반 간호사는 토큰으로 조회하므로 group_id 불필요, 관리자는 선택된 그룹 ID 사용
  // 그룹 권위 소스:
  //   - ADM: 드롭다운 선택값(selectedGroupId)
  //   - HN/일반: loginUser.group_id (= switch-group 으로 갱신되는 '현재 보고 있는 병동' 토큰).
  //     이 페이지는 useGroupSelection/useInitializeGroup 을 호출하지 않아 store.selectedGroupId 가
  //     비어있을 수 있으므로, HN 의 현재 병동은 토큰 group_id 를 직접 사용한다.
  //     (백엔드 resolve_effective_group 이 managed 검증하므로 안전.)
  const gid = isAdmin ? (selectedGroupId ?? "") : (loginUser?.group_id ?? "");
  const canEditMonthlyMemo =
    Boolean(loginUser?.nurse_id) &&
    (!isAdmin || !gid || gid === loginUser?.group_id);
  const {
    data: monthlyMemoData,
    isPending: isMonthlyMemoLoading,
    isError: isMonthlyMemoError,
    refetch: refetchMonthlyMemo,
  } = useMonthlyMemoQuery(activeDate?.year, activeDate?.month, gid);
  const { mutateAsync: saveMonthlyMemo } = useSaveMonthlyMemo();
  const handleSaveMonthlyMemo = useCallback(
    async (memo: string) => {
      if (!activeDate) return;
      await saveMonthlyMemo({
        year: activeDate.year,
        month: activeDate.month,
        groupId: gid,
        memo,
      });
    },
    [activeDate, gid, saveMonthlyMemo]
  );

  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const appliedWantedUrlKey = useRef<string | null>(null);

  const setActiveDateSynced = useCallback(
    (date: { year: number; month: number } | null) => {
      setActiveDate(date);
      if (date) {
        const sp = new URLSearchParams(location.search);
        sp.set("year", String(date.year));
        sp.set("month", String(date.month));
        appliedWantedUrlKey.current = `${gid}|?${sp.toString()}`;
        setSearchParams(sp, { replace: true });
      }
    },
    [gid, location.search, setSearchParams]
  );

  // 페이지 진입 시 body에 클래스 추가 (배경색 적용을 위함.)
  useEffect(() => {
    document.body.classList.add("page-roster-wanted-back");

    return () => {
      document.body.classList.remove("page-roster-wanted-back");
    };
  }, []);

  // textarea 내용 변경 시 높이 자동 조정
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setWantedInput(e.target.value);
    adjustTextareaHeight(e.target);
  };

  // 통합 리소스 훅 사용
  const {
    data: resources,
    isLoading,
    error: pageError,
  } = useRosterWantedResources({
    year: activeDate?.year,
    month: activeDate?.month,
    groupId: gid,
    nurseId: loginUser?.nurse_id,
    isHeadNurse: loginUser?.is_head_nurse,
  });

  const {
    nurseList,
    wantedList,
    shiftsData,
    shiftLookupMaps,
    holidayDatesData,
    myWeeklyOffData,
    wantedConfig,
    preferencesLatestData,
    preferencesAllData,
    currentWanted,
    isCurrentWantedClosed,
    serverConfig,
  } = resources || {};
  console.log("shiftsData:", shiftsData);

  // Mid 사용 여부
  const isUseMid = serverConfig?.use_mid ?? false;

  // 원티드 희망사항 제출 Mutation 훅
  const {
    mutate: submitWantedRequest,
    isPending: isSubmitWantedPending,
    isSuccess: isSubmitWantedSuccess,
    isError: isSubmitWantedError,
    reset: resetSubmitWanted,
  } = useSaveWantedRequest();
  // 캘린더 저장 Mutation 훅
  const {
    mutateAsync: saveCalendarData,
    isPending: isSaveCalendarPending,
    isSuccess: isSaveCalendarSuccess,
    isError: isSaveCalendarError,
    reset: resetSaveCalendar,
  } = useSaveCalendarData();

  const { mutateAsync: finalSubmitMutation, isPending: isFinalSubmitPending } =
    useFinalSubmit();

  const {
    mutateAsync: retractSubmissionMutation,
    isPending: isRetractPending,
  } = useRetractSubmission();

  const isPending =
    isSubmitWantedPending ||
    isSaveCalendarPending ||
    isFinalSubmitPending ||
    isRetractPending;
  const isSuccess = isSubmitWantedSuccess || isSaveCalendarSuccess;
  const isError = isSubmitWantedError || isSaveCalendarError;

  const dailyMaxRequests = useMemo(() => {
    const map: Record<string, number> = {};
    if (wantedConfig && Array.isArray(wantedConfig)) {
      wantedConfig.forEach((item) => {
        if (item.target_date && item.max_requests > 0) {
          map[item.target_date] = item.max_requests;
        }
      });
    }
    return map;
  }, [wantedConfig]);

  // TODO : OFF/휴가/휴무/연차인 근무코드 추출 (제한 걸 때 사용) 일단은 이렇게 적용하고 추후 기획 변경될 수도 있음.
  const targetShiftIds = useMemo(() => {
    return availableShifts
      .filter(
        (shift) =>
          ["OFF", "휴가", "휴무", "연차"].includes(shift.type) ||
          shift.shift_id === "O"
      )
      .map((s) => s.shift_id);
  }, [availableShifts]);

  // 일자별 신청 수 집계 (currentCount 계산) - 실시간 반영을 위해 useMemo 사용
  const dailyRequestCounts = useMemo(() => {
    const counts: Record<string, number> = {};

    try {
      if (!preferenceSchedule || !availableShifts || !activeDate) return counts;

      // 1. 서버 데이터(preferenceSchedule)에서 '나'를 제외한 다른 사람들의 신청 내역 집계
      if (Array.isArray(preferenceSchedule)) {
        preferenceSchedule.forEach((nurse) => {
          // if (nurse.nurse_id === loginUser?.nurse_id) return; // 내 데이터 포함 (제출된 데이터 기준)

          nurse.schedule.forEach((shiftCode, dayIndex) => {
            if (shiftCode && targetShiftIds.includes(shiftCode)) {
              const day = dayIndex + 1;
              const dateStr = `${activeDate.year}-${String(activeDate.month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              counts[dateStr] = (counts[dateStr] || 0) + 1;
            }
          });
        });
      }
    } catch (error) {
      console.error("dailyRequestCounts calculation error:", error);
    }
    return counts;
  }, [preferenceSchedule, availableShifts, activeDate, targetShiftIds]);

  useEffect(() => {
    if (!nurseList || !Array.isArray(nurseList)) return;
    const ordered = sortNurseListByRoleThenSequence([...nurseList]);
    const extract = ordered.map(({ nurse_id, name }) => ({
      nurse_id,
      name,
    }));
    setFullNurseSchema(ordered);
    setNurseSchema_IdName(extract);
  }, [nurseList]);

  const currentNurse = useMemo(() => {
    return fullNurseSchema.find((n) => n.nurse_id === loginUser?.nurse_id);
  }, [fullNurseSchema, loginUser]);

  useEffect(() => {
    if (wantedList && Array.isArray(wantedList)) {
      setFullWanted(wantedList);
    }
  }, [wantedList]);

  useLayoutEffect(() => {
    if (!wantedList?.length) return;

    const syncKey = `${gid}|${location.search}`;
    if (appliedWantedUrlKey.current === syncKey) return;

    const params = new URLSearchParams(location.search);
    const qy = params.get("year");
    const qm = params.get("month");

    if (qy && qm) {
      const y = parseInt(qy, 10);
      const m = parseInt(qm, 10);
      if (!Number.isNaN(y) && !Number.isNaN(m)) {
        const matched = wantedList.find(
          (w) => Number(w.year) === y && Number(w.month) === m
        );
        if (matched) {
          setActiveDate({ year: y, month: m });
          appliedWantedUrlKey.current = syncKey;
          return;
        }
        toast.error(`${y}년 ${m}월 원티드를 찾을 수 없습니다.`, {
          description:
            "요청 가능한 기간이 아니거나 잘못된 경로일 수 있습니다. 가장 최근 요청 기간을 표시합니다.",
        });
        setActiveDate({
          year: Number(wantedList[0].year),
          month: Number(wantedList[0].month),
        });
        appliedWantedUrlKey.current = syncKey;
        return;
      }
    }

    setActiveDate({
      year: Number(wantedList[0].year),
      month: Number(wantedList[0].month),
    });
    appliedWantedUrlKey.current = syncKey;
  }, [wantedList, location.search, gid]);

  /* 근무 희망사항 제출  */
  // mutate로 수정해야함
  function submitRequest() {
    if (!activeDate) return;
    // 중복 호출 방지: 이미 요청 중이면 무시
    if (isSubmitRequest) {
      return;
    }
    if (!wantedInput) {
      alert("희망 근무 내용을 입력해주세요.");
      return;
    }

    setIsSubmitRequest(true);

    submitWantedRequest(
      {
        wantedInput,
        targetData: selectedCalendarData,
        avoidShiftByDate,
        activeDate,
        isReset: false,
        groupId: gid,
        managesAvoid: true,
      },
      {
        onSuccess: async () => {
          // 알림 리스트 캐싱 무효화 (팝오버, 모달 모두)
          await queryClient.invalidateQueries({ queryKey: ["alarms"] });

          setTimeout(() => {
            setIsSubmitRequest(false);
            resetSubmitWanted();
          }, 1000);
          // 휴무 한도 초과는 더 이상 "일부만 저장 + 경고 모달" 이 아니라 422 로
          // 전체가 거절된다(달력 저장과 같은 계약). 메시지는 axios 인터셉터가
          // detail.message 를 그대로 띄운다.
        },
      }
    );
  }

  /* 캘린더 저장 */
  const submitCalendarData = async (
    overrideData?: Type_SelectCalendar,
    isReset: boolean = false
  ) => {
    if (!activeDate) return;
    // 중복 호출 방지: 이미 요청 중이면 무시
    if (isSubmitRequest) {
      return;
    }
    setIsSubmitRequest(true);

    const targetData = overrideData ?? selectedCalendarData;
    const targetAvoidData = isReset ? {} : avoidShiftByDate;

    try {
      await saveCalendarData({
        targetData,
        avoidShiftByDate: targetAvoidData,
        activeDate,
        isReset,
        groupId: gid,
        managesAvoid: true,
      });
      setInitialSelectedCalendarData(targetData);
      setInitialAvoidShiftByDate(targetAvoidData);
      setInitialWantedInput(wantedInput);
      setHasUnsavedChanges(false);
      setTimeout(() => {
        setIsSubmitRequest(false);
        resetSaveCalendar();
      }, 1000);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "저장 중 오류가 발생했습니다.";
      console.error("submitCalendarData error:", errorMsg);
      setIsSubmitRequest(false); // 에러 발생 시 모달 닫기 (Toast로 에러 표시)
      throw error; // 상위 호출자(handleReset 등)에게 에러 전파
    }
  };

  // 최종 제출
  const finalSubmit = async () => {
    if (!activeDate) return;

    try {
      await finalSubmitMutation({
        managesAvoid: true,
        targetData: selectedCalendarData,
        avoidShiftByDate,
        activeDate,
        isReset: false,
        groupId: gid,
      });

      toast.success("성공적으로 제출되었습니다.");
      setConfirmModal(null);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "제출 중 오류가 발생했습니다.";
      console.error("finalSubmit error:", errorMsg);
      toast.error(errorMsg);
    }
  };

  // 제출 철회
  const retractSubmission = async () => {
    if (!activeDate) return;

    try {
      await retractSubmissionMutation({
        year: activeDate.year,
        month: activeDate.month,
        groupId: gid,
      });

      toast.success("제출이 철회되었습니다. 다시 수정할 수 있습니다.");
      setConfirmModal(null);
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : "철회 중 오류가 발생했습니다.";
      console.error("retractSubmission error:", errorMsg);
      toast.error(`철회에 실패했습니다: ${errorMsg}`);
    }
  };

  // 리셋
  const handleReset = async () => {
    if (!activeDate) return;
    if (!confirm("초기화하시겠습니까?")) {
      return;
    }
    try {
      // 일단 state만 초기화
      setSelectedCalendarData({});
      setWantedInput("");
      setSelectedShiftType("");
      setAvoidShiftByDate({});

      // 빈 데이터로 저장 (재사용)
      await submitCalendarData({}, true);
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.preferences.latest(
          activeDate.year,
          activeDate.month,
          gid
        ),
      });
      toast.success("초기화되었습니다.");
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : "초기화 중 오류가 발생했습니다.";
      console.error("handleReset error:", errorMsg);
      toast.error(errorMsg);
    }
  };

  const discardChanges = useCallback(() => {
    setSelectedCalendarData(initialSelectedCalendarData);
    setAvoidShiftByDate(initialAvoidShiftByDate);
    setWantedInput(initialWantedInput);
    setHasUnsavedChanges(false);
  }, [
    initialSelectedCalendarData,
    initialAvoidShiftByDate,
    initialWantedInput,
  ]);

  const { showNavigationModal, proceedNavigation, handleCancelNavigation } =
    useUnsavedChanges(hasUnsavedChanges, discardChanges);

  const handleSaveAndNavigate = async () => {
    await submitCalendarData();
    setHasUnsavedChanges(false);
    proceedNavigation();
  };

  useEffect(() => {
    if (isLoading) return;
    const calendarChanged =
      JSON.stringify(initialSelectedCalendarData) !==
      JSON.stringify(selectedCalendarData);
    const avoidChanged =
      JSON.stringify(initialAvoidShiftByDate) !==
      JSON.stringify(avoidShiftByDate);
    const inputChanged = (initialWantedInput || "") !== (wantedInput || "");
    setHasUnsavedChanges(calendarChanged || avoidChanged || inputChanged);
  }, [
    selectedCalendarData,
    avoidShiftByDate,
    wantedInput,
    initialSelectedCalendarData,
    initialAvoidShiftByDate,
    initialWantedInput,
    isLoading,
  ]);

  const handleAvoidShiftRemove = useCallback((dateStr: string) => {
    setAvoidShiftByDate((previous) => {
      const next = { ...previous };
      delete next[dateStr];
      return next;
    });
  }, []);

  const handleShiftSelection = useCallback(
    (shiftId: string) => {
      if (submittedStatus !== "requested") return;

      setSelectedShiftType((currentShiftId) =>
        currentShiftId === shiftId ? "" : shiftId
      );
    },
    [submittedStatus]
  );

  const handleCalendarModeChange = useCallback(
    (mode: CalendarDisplay) => {
      if (submittedStatus === "empty") return;

      setCalendarDisplay(mode);
      setActiveIntent(
        submittedStatus === "requested" && mode !== "all" ? mode : null
      );
    },
    [submittedStatus]
  );

  const handleMissingShiftSelection = useCallback(() => {
    const now = Date.now();
    if (now - lastMissingShiftSelectionAtRef.current < 700) return;

    lastMissingShiftSelectionAtRef.current = now;
    setIsShiftPickerHighlighted(true);
    toast.info("근무코드를 먼저 선택해 주세요.");
    if (missingShiftSelectionTimerRef.current !== null) {
      window.clearTimeout(missingShiftSelectionTimerRef.current);
    }
    missingShiftSelectionTimerRef.current = window.setTimeout(() => {
      setIsShiftPickerHighlighted(false);
      missingShiftSelectionTimerRef.current = null;
    }, 700);
  }, []);

  const handleCalendarDateClick = async (
    dateStr: string,
    intent: ShiftPreferenceMode,
    comment?: string,
    shiftId?: string,
    skipReplacementConfirmation = false
  ) => {
    if (submittedStatus !== "requested") return;

    const targetShiftType = shiftId?.trim() || selectedShiftType.trim();

    if (!targetShiftType) {
      toast.info("근무코드를 먼저 선택해 주세요.");
      return;
    }

    const currentData = selectedCalendarData[dateStr];
    const isWeeklyOff = currentData?.comment === "주휴";

    if (isWeeklyOff) return;

    const currentAvoidEntry = avoidShiftByDate[dateStr];
    const currentShiftCode = currentData?.type ?? currentAvoidEntry?.shiftId;
    const currentIntent: ShiftPreferenceMode | null = currentData?.type
      ? "wanted"
      : currentAvoidEntry
        ? "avoid"
        : null;
    const needsReplacementConfirmation =
      !skipReplacementConfirmation &&
      currentShiftCode &&
      currentIntent &&
      currentShiftCode !== targetShiftType;

    if (needsReplacementConfirmation) {
      setConfirmModal({
        type: "replacement",
        dateStr,
        intent,
        comment: comment ?? "",
        currentShiftCode,
        currentIntent,
        nextShiftCode: targetShiftType,
      });
      return;
    }

    if (intent === "avoid") {
      if (currentData?.type) {
        setSelectedCalendarData((previous) => {
          const next = { ...previous };
          delete next[dateStr];
          return next;
        });
        toast.info(
          `${Number(dateStr.split("-")[2])}일 ${currentData.type} 희망 근무를 ${targetShiftType} 피하고 싶은 근무로 변경했습니다.`
        );
      }

      setAvoidShiftByDate((previous) => {
        const previousEntry = previous[dateStr];
        const nextComment = comment?.trim() || null;

        if (
          previousEntry?.shiftId === targetShiftType &&
          previousEntry.comment === nextComment
        ) {
          return previous;
        }

        return {
          ...previous,
          [dateStr]: { shiftId: targetShiftType, comment: nextComment },
        };
      });
      return;
    }

    if (currentAvoidEntry) {
      setAvoidShiftByDate((previous) => {
        const next = { ...previous };
        delete next[dateStr];
        return next;
      });
    }

    // 개인별 원티드 개수 제한 체크
    const maxPersonalWanted = currentNurse?.wanted_max_requests ?? null;

    const isTargetShift = targetShiftIds.includes(targetShiftType);

    if (isTargetShift && maxPersonalWanted !== null) {
      let myCurrentCount = 0;
      Object.values(selectedCalendarData).forEach((entry) => {
        if (entry.type && targetShiftIds.includes(entry.type)) {
          myCurrentCount++;
        }
      });

      const isAlreadyCounted =
        currentData?.type && targetShiftIds.includes(currentData.type);

      if (!isAlreadyCounted) {
        if (myCurrentCount >= maxPersonalWanted) {
          setConfirmModal({ type: "totalExceeded" });
          return;
        }
      }
    }

    // 근무코드의 type이 휴가, 연차, 휴무인 경우에만 유효성 검사 수행
    const targetShift = availableShifts.find(
      (s) => s.shift_id === targetShiftType
    );
    const shouldValidate =
      targetShift && ["휴가", "연차", "휴무"].includes(targetShift.type);

    if (shouldValidate) {
      const maxCount = dailyMaxRequests[dateStr];
      if (maxCount !== undefined) {
        const currentCount = dailyRequestCounts[dateStr] || 0;

        // 내가 이미 제출한 스케줄인지 확인
        const day = parseInt(dateStr.split("-")[2], 10);
        const mySubmittedSchedule = preferenceSchedule?.find(
          (n) => n.nurse_id === loginUser?.nurse_id
        )?.schedule;
        const mySubmittedShift = mySubmittedSchedule?.[day - 1];
        const isAlreadySubmittedTargetShift =
          mySubmittedShift && targetShiftIds.includes(mySubmittedShift);

        if (!isAlreadySubmittedTargetShift) {
          if (currentCount >= maxCount) {
            setConfirmModal({ type: "dateExceeded", date: dateStr });

            return;
          }
        }
      }
    }

    const isReplacingShift = Boolean(
      currentData?.type && currentData.type !== targetShiftType
    );

    if (isReplacingShift) {
      const replacementNotice = `${Number(dateStr.split("-")[2])}일 근무가 ${currentData.type}에서 ${targetShiftType}로 변경되었습니다.`;
      toast.info(
        currentData?.comment
          ? `${replacementNotice} 기존 사유는 초기화되었습니다.`
          : replacementNotice
      );
    }

    setSelectedCalendarData((prev) => ({
      ...prev,
      [dateStr]: {
        type: targetShiftType,
        comment: comment?.trim() || null,
      },
    }));
  };

  // 휴일 세팅
  useEffect(() => {
    if (holidayDatesData) setHolidayDates(holidayDatesData);
  }, [holidayDatesData]);

  useEffect(() => {
    if (shiftsData) {
      setAvailableShifts(shiftsData);
    }
  }, [shiftsData]);

  // 주휴 세팅
  useEffect(() => {
    setMyWeeklyOffIndex(myWeeklyOffData?.my_weekly_off_weekday ?? null);
  }, [myWeeklyOffData]);

  useEffect(() => {
    if (!activeDate) return;

    if (preferencesLatestData) {
      setPreferenceData(preferencesLatestData.preference_data);
      setWantedInput(preferencesLatestData.preference_data?.request ?? "");
      setInitialWantedInput(
        preferencesLatestData.preference_data?.request ?? ""
      );
      setSubmittedStatus(preferencesLatestData.submission.status);

      const calendarData: CalendarData = {};
      if (preferencesLatestData.preference_data?.shift) {
        const shifts = preferencesLatestData.preference_data.shift;
        const maps = shiftLookupMaps;
        for (const [shiftType, dates] of Object.entries(shifts)) {
          if (!dates || typeof dates !== "object") continue;
          const dateList = Array.isArray(dates) ? dates : Object.keys(dates);
          for (const dateStr of dateList) {
            let formattedDate: string;
            if (dateStr.includes("-")) {
              formattedDate = dateStr;
            } else {
              const day = parseInt(dateStr, 10);
              formattedDate = `${activeDate.year}-${String(
                activeDate.month
              ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            }
            const cell = (dates as Record<string, type.PreferenceShiftDayCell>)[
              dateStr
            ];
            if (!cell) continue;
            const comment =
              cell.comment === null || cell.comment === undefined
                ? ""
                : String(cell.comment);
            const pk = cell.shifts_table_id;
            /** `Roster_create` / `row_NurseRosterList`와 동일: `wanted-shift-util` → `resolveShiftFromScheduleCellFromMaps` */
            const resolved = maps
              ? resolveShiftFromWantedEntry(
                  { shift_id: shiftType, shifts_table_id: pk ?? null },
                  maps
                )
              : undefined;
            const displayShiftId = resolved?.shift_id ?? shiftType;
            calendarData[formattedDate] = {
              shift: displayShiftId,
              comment,
            };
          }
        }
      }
      const legacyCalendarData: Type_SelectCalendar = Object.fromEntries(
        Object.entries(calendarData).map(([date, value]) => {
          const { shift, ...rest } = value;
          return [date, { ...rest, type: shift, comment: value.comment }];
        })
      );
      const wantedEntries =
        preferencesLatestData.preference_data?.wanted_entries;
      const persistedEntries = Array.isArray(wantedEntries)
        ? splitWantedEntries(wantedEntries)
        : null;
      const selectedEntries =
        persistedEntries?.wantedByDate ?? legacyCalendarData;
      const avoidEntries = persistedEntries?.avoidByDate ?? {};

      setSelectedCalendarData(selectedEntries);
      setInitialSelectedCalendarData(selectedEntries);
      setAvoidShiftByDate(avoidEntries);
      setInitialAvoidShiftByDate(avoidEntries);
    } else if (!isLoading) {
      setPreferenceData(null);
      setWantedInput("");
      setInitialWantedInput("");
      setSubmittedStatus(
        isCurrentWantedClosed ? "closed" : currentWanted ? "requested" : "empty"
      );
      setSelectedCalendarData({});
      setInitialSelectedCalendarData({});
      setAvoidShiftByDate({});
      setInitialAvoidShiftByDate({});
    }
  }, [
    activeDate,
    preferencesLatestData,
    isLoading,
    isCurrentWantedClosed,
    currentWanted,
    shiftLookupMaps,
  ]);

  useEffect(() => {
    if (preferencesAllData && fullNurseSchema.length > 0 && activeDate) {
      const arrayLength = new Date(
        activeDate.year,
        activeDate.month,
        0
      ).getDate();
      const fullNurse_preference = fullNurseSchema.map((nurse) => {
        const found = preferencesAllData.find(
          (p) => p.nurse_id === nurse.nurse_id
        );
        return {
          nurse_id: nurse.nurse_id,
          schedule: preferenceToSchedule(arrayLength, found),
        };
      });
      setPreferenceSchedule(fullNurse_preference);
    } else if (!isLoading) {
      // isPreferencesAllLoading 대체
      setPreferenceSchedule([]);
    }
  }, [preferencesAllData, isLoading, fullNurseSchema, activeDate]);

  useEffect(() => {
    if (availableShifts.length > 0) {
      const filteredShifts = availableShifts.filter((item) => {
        if (!isUseMid && item.default_shift === "M") return false;
        const isDefaultShiftExcluding주 =
          item.default_shift && item.default_shift !== "주";
        return (
          (isDefaultShiftExcluding주 || item.shift_gb === null) &&
          item.show_in_preference
        );
      });
      setDefaultShiftCode(filteredShifts);
    }
  }, [availableShifts, isUseMid]);

  useEffect(() => {
    const textarea = document.getElementById(
      "wanted-input"
    ) as HTMLTextAreaElement;
    if (textarea) {
      adjustTextareaHeight(textarea);
    }
  }, []);

  const requestLimitLabel =
    preferencesLatestData?.request_limit?.max == null
      ? "제한 없음"
      : `${preferencesLatestData.request_limit.used} / ${preferencesLatestData.request_limit.max}`;
  const deadlineLabel = formatWantedDeadline(
    preferencesLatestData?.submission.deadline_at
  );
  const submittedAtLabel = formatWantedSubmissionAt(
    preferencesLatestData?.submission.submitted_at
  );

  const isEditableWanted = submittedStatus === "requested";
  const avoidShiftCount = Object.keys(avoidShiftByDate).length;
  const wantedShiftCount = Object.values(selectedCalendarData).filter(
    (entry) => entry.type && entry.comment !== "주휴"
  ).length;
  const calendarDisplaySummary =
    calendarDisplay === "wanted"
      ? { label: "희망해요", count: wantedShiftCount, tone: "bg-sig" }
      : calendarDisplay === "avoid"
        ? {
            label: "피하고 싶어요",
            count: avoidShiftCount,
            tone: "bg-amber-400",
          }
        : {
            label: "전체",
            count: wantedShiftCount + avoidShiftCount,
            tone: "bg-text-sub",
          };
  const activeIntentSummary =
    activeIntent === "avoid"
      ? {
          label: "피하고 싶어요",
          tone: "bg-amber-400",
          description: selectedShiftType
            ? "빈 날짜를 누르면 바로 임시 반영됩니다."
            : "적용할 근무코드를 먼저 선택하세요.",
        }
      : activeIntent === "wanted"
        ? {
            label: "희망해요",
            tone: "bg-sig",
            description: selectedShiftType
              ? "빈 날짜를 누르면 바로 반영됩니다."
              : "적용할 근무코드를 먼저 선택하세요.",
          }
        : null;
  const editorState = getWantedEditorState({
    submissionStatus: submittedStatus,
    hasSavedDraft: Boolean(preferencesLatestData?.preference_data),
    hasUnsavedChanges,
  });
  const canResetWanted =
    Object.keys(selectedCalendarData).length > 0 ||
    Boolean(wantedInput) ||
    Boolean(preferenceData?.preference?.length);
  const pairPreferenceCount = preferenceData?.preference?.length ?? 0;

  if (pageError) {
    const errorMsg =
      pageError instanceof Error
        ? pageError.message
        : "데이터를 불러오는 중 오류가 발생했습니다.";
    return <ErrorComponent errorMsg={errorMsg} />;
  }

  if (!isLoading && wantedList && wantedList.length === 0) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <Info className="w-16 h-16 text-[#939399] mb-4" />
        <h2 className="text-[20px] font-bold text-[#939399] mb-2">
          요청 가능한 원티드 기간이 없습니다.
        </h2>
        <p className="text-[#939399] font-pretendard">
          수간호사에게 문의해주세요.
        </p>
      </div>
    );
  }

  if (isLoading || !activeDate) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <LoadingDots />
      </div>
    );
  }

  return (
    <main
      className={`roster-wanted-back mx-auto w-full max-w-[1520px] px-4 py-4 font-pretendard sm:px-6 sm:py-6 lg:px-8 ${isEditableWanted ? "pb-28 sm:pb-32" : ""}`}
    >
      <WantedStatusBanner
        title="원티드 작성"
        actions={
          loginUser?.is_head_nurse ? (
            <HeadNurseHeader
              wantedButtonRef={wantedButtonRef}
              isSubmitRequest={isSubmitRequest}
              isFinalSubmitButtonClick={isFinalSubmitPending}
              activeDate={activeDate}
              wantedList={wantedList}
              setActiveDate={setActiveDateSynced}
              selectedGroupId={gid}
              showMonthSelect={false}
            />
          ) : undefined
        }
      />

      <div className="min-w-0">
        <section
          aria-label="원티드 캘린더"
          className="min-w-0 px-4 pb-6 pt-2 sm:px-6"
        >
          <div className="pb-1">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                {loginUser && (
                  <HeadNurseHeader
                    isSubmitRequest={isSubmitRequest}
                    isFinalSubmitButtonClick={isFinalSubmitPending}
                    activeDate={activeDate}
                    wantedList={wantedList}
                    setActiveDate={setActiveDateSynced}
                    selectedGroupId={gid}
                    showDeadlineControl={false}
                    showMonthLabel={false}
                  />
                )}
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs">
                  <span className="font-medium text-text-sub">마감</span>
                  <strong className="font-semibold text-text-main2">
                    {deadlineLabel}
                  </strong>
                </span>
                <span className="inline-flex min-h-9 items-center gap-2 rounded-full bg-light-muted px-3 text-xs text-text-main2">
                  <span className="font-medium">휴가·휴무 신청 한도</span>
                  <strong className="font-bold text-sig">
                    {requestLimitLabel}
                  </strong>
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-text-sub sm:justify-end">
                <div className="flex items-center gap-2">
                  {currentNurse?.enable_aide && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 border-border bg-background px-2.5 hover:bg-light-muted"
                      aria-label="Aide 자연어 요청"
                      title="자연어 요청"
                      onClick={() => setIsAideDialogOpen(true)}
                      disabled={
                        !isEditableWanted ||
                        isSubmitRequest ||
                        isFinalSubmitPending ||
                        isRetractPending
                      }
                    >
                      <img
                        src={images.logo_aide}
                        alt=""
                        width={48}
                        height={17}
                        className="h-4 w-auto"
                      />
                    </Button>
                  )}
                  <WantedMonthlyMemo
                    year={activeDate.year}
                    month={activeDate.month}
                    groupId={gid}
                    memo={monthlyMemoData?.monthly_memo}
                    canEdit={canEditMonthlyMemo}
                    nurseId={loginUser?.nurse_id}
                    isMemoLoading={isMonthlyMemoLoading}
                    isMemoError={isMonthlyMemoError}
                    onSave={handleSaveMonthlyMemo}
                    onRetryLoad={() => {
                      void refetchMonthlyMemo();
                    }}
                  />
                  {currentNurse?.enable_nurse_pair_preference && (
                    <Popover>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="relative size-9 before:absolute before:-inset-1"
                              aria-label={`희망동료 설정${pairPreferenceCount > 0 ? `, ${pairPreferenceCount}명 등록됨` : ""}`}
                              title="희망동료 설정"
                            >
                              <UsersRound aria-hidden="true" />
                              {pairPreferenceCount > 0 ? (
                                <span className="pointer-events-none absolute -right-1.5 -top-1.5 inline-flex size-5 items-center justify-center rounded-full bg-sky-100 text-[11px] font-bold leading-none text-sig ring-2 ring-background">
                                  {pairPreferenceCount > 9
                                    ? "9+"
                                    : pairPreferenceCount}
                                </span>
                              ) : null}
                            </Button>
                          </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent>희망동료 설정</TooltipContent>
                      </Tooltip>
                      <PopoverContent
                        align="end"
                        className="max-h-[min(70vh,28rem)] w-[min(calc(100vw-2rem),24rem)] max-w-none overflow-y-auto p-4"
                        aria-label="희망동료 선호 및 비선호"
                      >
                        <PairPreferenceEditor
                          preferenceData={preferenceData ?? undefined}
                          setPreferenceData={setPreferenceData}
                          nurseSchema_IdName={nurseSchema_IdName}
                          currentNurseId={loginUser?.nurse_id}
                          isEditable={isEditableWanted}
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                  {canViewAllStaffWantedOverview && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="relative size-9 before:absolute before:-inset-1"
                          aria-label="전체 근무자 원티드 요청 현황"
                          title="전체 근무자 원티드 요청 현황"
                          onClick={() =>
                            setConfirmModal({
                              type: "submittedList",
                              isViewOnlyOff: false,
                              showWeeklyOff: false,
                            })
                          }
                        >
                          <ClipboardList aria-hidden="true" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>전체 근무자 원티드 요청 현황</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </div>

          {isWantedGuideVisible && (
            <div
              role="note"
              className="mt-2 flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-xs leading-5 text-blue-700"
            >
              <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <p className="min-w-0 flex-1">
                주휴는 신청 개수에 포함되지 않으며, 캘린더 숫자는 휴가·휴무 신청
                가능 횟수입니다. 제출한 원티드만 근무표에 반영됩니다.
                <span className="mt-1 block break-keep">
                  희망해요는 원하는 날을, 피하고 싶어요는 피하고 싶은 날을
                  선택합니다. 탭을 선택하면 해당 요청을 캘린더에서 확인하고
                  작성할 수 있습니다.
                </span>
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-5 shrink-0 rounded text-blue-700 hover:bg-sky-100 hover:text-blue-800"
                aria-label="원티드 신청 안내 닫기"
                onClick={() => setIsWantedGuideVisible(false)}
              >
                <X aria-hidden="true" className="size-3.5" />
              </Button>
            </div>
          )}

          {editorState === "closed" && (
            <div
              role="status"
              className="mt-3 flex items-center gap-2 rounded-lg border border-sig/15 bg-sig/5 px-3 py-2 text-xs font-medium text-sig"
            >
              <Info aria-hidden="true" className="size-4 shrink-0" />
              <span>
                이 원티드 작성 기간은 마감되었습니다. 신청 내용을 수정할 수
                없습니다.
              </span>
            </div>
          )}

          {editorState === "submitted" && (
            <div
              role="status"
              className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-sig/20 bg-sig/5 px-3 py-2.5 text-sm"
            >
              <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-sig/10 text-sig">
                <Check aria-hidden="true" className="size-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-text-main2">
                  원티드 제출이 완료되었습니다.
                </span>
                {submittedAtLabel ? (
                  <span className="mt-0.5 block text-xs leading-5 text-text-sub [font-variant-numeric:tabular-nums]">
                    제출 시각 · {submittedAtLabel}
                  </span>
                ) : null}
              </span>
              <Button
                type="button"
                variant="destructive"
                className="min-h-9 shrink-0 font-semibold shadow-sm"
                onClick={() => setConfirmModal({ type: "retract" })}
                disabled={
                  isRetractPending || isFinalSubmitPending || isSubmitRequest
                }
              >
                {isRetractPending ? "철회 중…" : "제출 철회"}
              </Button>
            </div>
          )}

          <div
            id="wanted-calendar-panel"
            role="tabpanel"
            aria-labelledby={
              calendarDisplay === "all"
                ? "wanted-intent-all"
                : calendarDisplay === "wanted"
                  ? "wanted-intent-wanted"
                  : "wanted-intent-avoid"
            }
            className="relative mt-4 rounded-[14px] border border-border/70 bg-background shadow-[0_18px_40px_-24px_rgba(15,23,42,0.28)]"
          >
            <div className="grid items-start gap-4 xl:grid-cols-[17.5rem_minmax(0,1fr)] xl:gap-0">
              <WantedAuthoringCard
                selectedShiftId={selectedShiftType}
                shifts={defaultShiftCode}
                disabled={!isEditableWanted}
                highlightSelection={isShiftPickerHighlighted}
                onShiftChange={handleShiftSelection}
                onClearSelection={() => handleShiftSelection("")}
              />
              <div className="contents">
                <div className="col-span-full order-first flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-4 pb-1 sm:px-5 xl:px-6">
                  <div
                    role="tablist"
                    aria-label="원티드 작성 모드"
                    className="flex flex-wrap items-center gap-1.5"
                  >
                    <button
                      id="wanted-intent-all"
                      type="button"
                      role="tab"
                      aria-controls="wanted-calendar-panel"
                      aria-selected={calendarDisplay === "all"}
                      title="전체 보기"
                      className={`inline-flex min-h-10 items-center justify-center rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 ${calendarDisplay === "all" ? "border-text-main2 text-text-main2" : "border-border text-text-sub hover:border-text-sub hover:text-text-main2"}`}
                      onClick={() => handleCalendarModeChange("all")}
                    >
                      전체
                    </button>
                    <button
                      id="wanted-intent-wanted"
                      type="button"
                      role="tab"
                      aria-controls="wanted-calendar-panel"
                      aria-selected={calendarDisplay === "wanted"}
                      className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 ${calendarDisplay === "wanted" ? "border-sig text-sig" : "border-border text-text-sub hover:border-sig/60 hover:text-sig"}`}
                      onClick={() => handleCalendarModeChange("wanted")}
                    >
                      <Check aria-hidden="true" className="size-3.5" />
                      희망해요
                    </button>
                    <button
                      id="wanted-intent-avoid"
                      type="button"
                      role="tab"
                      aria-controls="wanted-calendar-panel"
                      aria-selected={calendarDisplay === "avoid"}
                      className={`inline-flex min-h-10 items-center justify-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1 ${calendarDisplay === "avoid" ? "border-amber-700 text-amber-800" : "border-border text-text-sub hover:border-amber-400 hover:text-amber-800"}`}
                      onClick={() => handleCalendarModeChange("avoid")}
                    >
                      <Ban aria-hidden="true" className="size-3.5" />
                      피하고 싶어요
                    </button>
                  </div>
                  <div
                    role="status"
                    aria-live="polite"
                    aria-label={
                      activeIntentSummary
                        ? `작성 모드: ${activeIntentSummary.label}. ${activeIntentSummary.description}`
                        : `현재 보기: ${calendarDisplaySummary.label}, ${calendarDisplaySummary.count}일`
                    }
                    className="inline-flex min-w-0 items-center gap-1.5 whitespace-normal text-pretty text-xs font-medium text-text-sub"
                  >
                    <span
                      aria-hidden="true"
                      className={`size-1.5 rounded-full ${activeIntentSummary?.tone ?? calendarDisplaySummary.tone}`}
                    />
                    {activeIntentSummary
                      ? `작성 모드: ${activeIntentSummary.label} · ${activeIntentSummary.description}`
                      : `현재 보기: ${calendarDisplaySummary.label} · ${calendarDisplaySummary.count}일`}
                  </div>
                </div>
                <div className="min-w-0 w-full px-3 pb-3 max-lg:overflow-x-auto sm:px-4 sm:pb-4 xl:px-6">
                  <TableSelectDate
                    activeDate={activeDate}
                    holidayDates={holidayDates}
                    availableShifts={availableShifts}
                    selectedCalendarData={selectedCalendarData}
                    setSelectedCalendarData={setSelectedCalendarData}
                    submittedStatus={submittedStatus}
                    selectedShiftType={selectedShiftType}
                    isExistWanted={isExistWanted}
                    myWeeklyOff={myWeeklyOffIndex}
                    enableCommentInput
                    dailyMaxRequests={dailyMaxRequests}
                    dailyRequestCounts={dailyRequestCounts}
                    avoidShiftByDate={avoidShiftByDate}
                    calendarDisplay={calendarDisplay}
                    activeIntent={activeIntent}
                    onAvoidShiftRemove={handleAvoidShiftRemove}
                    onDateClick={handleCalendarDateClick}
                    onMissingShiftSelection={handleMissingShiftSelection}
                    onLockedDateClick={() =>
                      toast.info("확정된 일정은 변경할 수 없습니다.")
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <WantedActionBar
        editorState={editorState}
        isBusy={isSubmitRequest || isFinalSubmitPending || isRetractPending}
        canReset={isEditableWanted && canResetWanted}
        canSave={isEditableWanted && hasUnsavedChanges}
        canSubmit={isEditableWanted}
        onReset={handleReset}
        onSave={() => void submitCalendarData()}
        onSubmit={() => setConfirmModal({ type: "submit" })}
      />

      {currentNurse?.enable_aide && (
        <Dialog open={isAideDialogOpen} onOpenChange={setIsAideDialogOpen}>
          <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto font-pretendard sm:max-w-lg">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <img
                  src={images.logo_aide}
                  alt="Aide"
                  width={88}
                  height={31}
                  className="h-6 w-auto object-contain"
                />
                <span className="text-xs font-semibold text-sig">
                  AI 입력 도우미
                </span>
              </div>
              <DialogTitle className="pt-2 text-base">
                에이드에게 원티드를 작성해주시면 캘린더에 입력해드릴게요.
              </DialogTitle>
              <DialogDescription className="leading-5">
                날짜와 근무코드를 자연스럽게 작성해 주세요. 사유는 선택
                사항입니다.
              </DialogDescription>
            </DialogHeader>

            <fieldset className="flex flex-wrap gap-2">
              <legend className="sr-only">Aide 입력 예시</legend>
              {aideRequestExamples.map((example) => (
                <button
                  key={example}
                  type="button"
                  className="min-h-9 rounded-full border border-border bg-background px-3 text-left text-xs font-medium text-text-sub transition-colors hover:border-sig/40 hover:text-sig focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => setWantedInput(example)}
                  disabled={!isEditableWanted || isSubmitRequest}
                >
                  {example}
                </button>
              ))}
            </fieldset>

            <div className="flex gap-2 rounded-md bg-light-muted p-3">
              <Sparkles
                aria-hidden="true"
                className="mt-1 size-4 shrink-0 text-sig"
              />
              <label htmlFor="wanted-input" className="sr-only">
                Aide에게 캘린더 입력 요청하기
              </label>
              <textarea
                id="wanted-input"
                name="wanted-aide-request"
                autoComplete="off"
                className="min-h-20 w-full resize-y border-0 bg-transparent py-1 text-sm leading-6 text-text-main2 outline-none placeholder:text-text-sub disabled:cursor-not-allowed disabled:text-text-sub"
                rows={2}
                placeholder="예: 8일 N, 12일 휴무를 희망합니다."
                value={isEditableWanted ? (wantedInput ?? "") : ""}
                onChange={handleTextareaChange}
                onKeyDown={(event) => func.handleKeydown(event, submitRequest)}
                disabled={!isEditableWanted}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                onClick={() => {
                  submitRequest();
                  setIsAideDialogOpen(false);
                }}
                disabled={
                  isSubmitRequest ||
                  !isEditableWanted ||
                  !(wantedInput ?? "").trim()
                }
              >
                {isSubmitRequest ? "반영 중…" : "캘린더에 반영"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Portal로 모달 렌더링 */}
      {isSubmitRequest &&
        createPortal(
          <div
            aria-live="polite"
            className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/70 px-4"
          >
            {isPending ? (
              <>
                <p className="text-white text-2xl font-bold mb-5">
                  희망사항을 확인하고 있어요
                </p>
                <LoadingDots />
              </>
            ) : isSuccess ? (
              <p className="text-white text-2xl font-bold">저장되었습니다.</p>
            ) : isError ? (
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="wanted-save-error-title"
                className="flex max-w-md flex-col items-center rounded-lg bg-background px-6 py-8 text-center shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
              >
                <p
                  id="wanted-save-error-title"
                  className="text-xl font-bold text-text-main2"
                >
                  저장에 실패했습니다.
                </p>
                <p className="mt-2 text-sm leading-5 text-text-sub">
                  실패가 반복되면 고객센터에 문의해주세요.
                </p>
                <button
                  type="button"
                  className="mt-6 min-h-11 rounded-md bg-sig px-6 text-sm font-semibold text-white transition-colors hover:bg-sig/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-2"
                  onClick={() => {
                    setIsSubmitRequest(false);
                    resetSubmitWanted();
                    resetSaveCalendar();
                  }}
                >
                  확인
                </button>
              </div>
            ) : null}
          </div>,
          document.body
        )}

      {confirmModal?.type === "submittedList" &&
        activeDate &&
        canViewAllStaffWantedOverview && (
          <ModalSubmittedWantedlist
            defaultShiftCode={defaultShiftCode ?? []}
            currentScheduleInfo={{ ...activeDate, version: 0 }}
            holidayDates={holidayDates}
            fullWanted={fullWanted}
            preferenceSchedule={preferenceSchedule ?? []}
            shiftInfo={availableShifts}
            nurses={fullNurseSchema}
            onClose={() => setConfirmModal(null)}
            isViewOnlyOff={confirmModal.isViewOnlyOff}
            showWeeklyOff={confirmModal.showWeeklyOff}
            loginUser={loginUser}
          />
        )}

      {/* 수간호사 전용 모달들 */}
      {showNavigationModal && (
        <CustomModal.Confirm
          isOpen={showNavigationModal}
          onClose={handleCancelNavigation}
          onConfirm={handleSaveAndNavigate}
          isPending={isSubmitRequest}
          title={`저장되지 않은 변경사항이 있습니다. \n저장하시겠습니까?`}
          pendingMsg="저장 중..."
        />
      )}

      {/* 단순 모달들 */}
      {/* 원티드 일자 개수 초과 모달 */}
      {confirmModal?.type === "dateExceeded" && (
        <CustomModal.Alert
          isOpen
          onClose={() => setConfirmModal(null)}
          title={`${Number(confirmModal.date.split("-")[1])}월 ${Number(confirmModal.date.split("-")[2])}일 신청 가능한 원티드 개수를\n초과하였습니다.`}
        />
      )}

      {/* 원티드 총 개수 초과 모달 */}
      {confirmModal?.type === "totalExceeded" && (
        <CustomModal.Alert
          isOpen
          onClose={() => setConfirmModal(null)}
          title={`신청가능한 휴가·휴무 개수(${currentNurse?.wanted_max_requests}개)를\n초과하였습니다.`}
        />
      )}

      {confirmModal?.type === "replacement" && (
        <CustomModal.Confirm
          isOpen
          title={
            <>
              기존 근무코드를 변경할까요?
              <br />
              <span className="mt-2 block text-sm leading-5 text-text-sub">
                {Number(confirmModal.dateStr.split("-")[2])}일의{" "}
                {confirmModal.currentShiftCode}{" "}
                {confirmModal.currentIntent === "wanted" ? "희망" : "피함"}{" "}
                신청을 {confirmModal.nextShiftCode}{" "}
                {confirmModal.intent === "wanted" ? "희망" : "피함"} 신청으로
                변경합니다.
              </span>
            </>
          }
          confirmText="변경하기"
          cancelText="취소"
          contentClassName="w-[min(calc(100vw-32px),24rem)]"
          onClose={() => setConfirmModal(null)}
          onConfirm={() => {
            void handleCalendarDateClick(
              confirmModal.dateStr,
              confirmModal.intent,
              confirmModal.comment,
              confirmModal.nextShiftCode,
              true
            );
            setConfirmModal(null);
          }}
        />
      )}
      <Dialog
        open={confirmModal?.type === "submit"}
        onOpenChange={(open) => {
          if (!open) setConfirmModal(null);
        }}
      >
        <DialogContent className="max-w-[calc(100vw-32px)] font-pretendard sm:max-w-[420px]">
          <DialogHeader className="space-y-1.5">
            <DialogTitle className="text-xl">원티드를 제출할까요?</DialogTitle>
            <DialogDescription className="text-sm leading-5">
              입력한 내용이 이번 근무표 작성에 반영됩니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-2 rounded-lg bg-light-muted/70 p-3.5 text-text-main2">
              <div className="border-r border-border/70 pr-3">
                <dt className="text-xs font-medium text-text-sub">희망 근무</dt>
                <dd className="mt-1 text-xl font-semibold leading-6">
                  {wantedShiftCount}일
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-text-sub">피할 근무</dt>
                <dd className="mt-1 text-xl font-semibold leading-6">
                  {avoidShiftCount}일
                </dd>
              </div>
            </dl>
            <p className="text-sm leading-5 text-text-sub">
              마감 전까지 철회하고 수정할 수 있어요.
              {deadlineLabel !== "마감일 없음" ? (
                <span className="ml-1 font-medium text-text-main2">
                  마감 {deadlineLabel}
                </span>
              ) : null}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmModal(null)}
              disabled={isFinalSubmitPending || isSubmitRequest}
            >
              취소
            </Button>
            <Button
              type="button"
              onClick={() => void finalSubmit()}
              disabled={isFinalSubmitPending || isSubmitRequest}
            >
              {isFinalSubmitPending || isSubmitRequest ? "제출 중…" : "제출"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmModal?.type === "retract"}
        onOpenChange={(open) => {
          if (!open) setConfirmModal(null);
        }}
      >
        <DialogContent className="max-w-[calc(100vw-32px)] font-pretendard sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>제출을 철회할까요?</DialogTitle>
            <DialogDescription className="leading-5">
              철회 뒤 수정한 내용은 반드시 다시 제출해야 합니다.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm leading-5 text-text-sub">
            <p>
              제출을 철회하면 현재 신청 내용이 근무표 생성 솔버 반영 대상에서
              제외될 수 있습니다.
            </p>
            <p>임시저장은 제출과 다르며, 솔버에 반영되지 않습니다.</p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmModal(null)}
              disabled={isRetractPending}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void retractSubmission()}
              disabled={isRetractPending}
            >
              {isRetractPending ? "철회 중…" : "제출 철회"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
